import { Dialog } from '@headlessui/react';
import Stack from './Stack';
import styled from 'styled-components';

const Overlay = styled.div`
    background-color: rgba(0, 0, 0, 0.2);
    position: fixed;
    inset: 0;
`;

const DefaultDialog = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    inset: 0;
    z-index: 51;
`;

export const ModalWrapper = styled(Stack)`
    position: relative;
    z-index: 1;
    max-width: 400px;
    padding: ${({ theme }) => theme.space_8}px;
    background-color: ${({ theme }) => theme.gray_1};
    border-radius: ${({ theme }) => theme.borderRadius_lg};
    font-family: ${({ theme }) => theme.style_primary};
    font-size: ${({ theme }) => theme.fontSize_base};
    ${({ $isHero }) => $isHero && `text-align: center;`}

    h2 {
        text-transform: capitalize;
        color: ${({ theme }) => theme.gray_7};
        font-weight: ${({ theme }) => theme.fontBold};
        font-family: inherit;
        font-size: 1.125em;
        line-height: 1.2em;
        margin-top: 0px;
    }

    p {
        color: ${({ theme }) => theme.gray_4};
        font-family: inherit;
        font-size: inherit;
        font-weight: normal;
        line-height: 1.25em;
        margin-top: 0px;
    }

    footer {
        display: flex;
        justify-content: space-between;
        font-size: 14px;
        ${({ $isHero }) =>
            $isHero &&
            `
            flex-direction: column;
            align-items: stretch;
        `}
    }

    a {
        color: ${({ theme }) => theme.gray_4};
        text-decoration: underline;
    }

    a:hover {
        color: ${({ theme }) => theme.gray_7};
    }
`;

export const SettingsModalWrapper = styled(Stack)`
    position: relative;
    z-index: 1;
    width: 500px;
    max-width: calc(100vw - ${({ theme }) => theme.space_8 * 2}px);
    max-height: calc(100vh - ${({ theme }) => theme.space_8 * 2}px);
    background-color: ${({ theme }) => theme.gray_7};
    border-radius: ${({ theme }) => theme.borderRadius_lg};
    box-shadow: 0px 3px 7px rgba(0, 0, 0, 0.12);
    font-family: ${({ theme }) => theme.style_primary};
    font-size: ${({ theme }) => theme.fontSize_base};
    overflow: hidden;

    header {
        border-bottom: 1px solid ${({ theme }) => theme.gray_5};
        padding: ${({ theme }) => `${theme.space_8}px ${theme.space_10}px`};
    }

    main {
        padding: ${({ theme }) => `${theme.space_9}px ${theme.space_10}px`};
    }

    footer {
        background-color: ${({ theme }) => theme.gray_6};
        border-top: 1px solid ${({ theme }) => theme.gray_5};
        padding: ${({ theme }) => `${theme.space_6}px ${theme.space_8}px`};
        display: flex;
        justify-content: flex-end;
    }

    h2 {
        margin-top: 0;
        padding: 0;
        font-family: inherit;
        font-weight: 600;
        font-size: 1rem;
        color: ${({ theme }) => theme.gray_2};
        text-transform: capitalize;
    }

    p {
        margin-top: 0;
        padding: 0;
        font-family: ${({ theme }) => theme.style_primary};
        font-weight: 400;
        font-size: 1rem;
        color: ${({ theme }) => theme.gray_3};
        line-height: 1.25em;
    }

    button {
        border-width: 1px;
        border-radius: 4px;
        font-weight: 500;
        min-width: unset;
    }

    a {
        color: ${({ theme }) => theme.purple_1};
        text-decoration: none;
    }

    a:hover {
        color: ${({ theme }) => theme.purple_1};
        text-decoration: underline;
    }
`;

export function ModalMain({ as = 'main', $space = '6px', ...props }) {
    return <Stack as={as} $space={$space} {...props} />;
}

function Modal({
    children,
    as = DefaultDialog,
    OverlayComponent = Overlay,
    ModalComponent = SettingsModalWrapper,
    ...props
}) {
    return (
        <Dialog as={as} {...props}>
            <OverlayComponent />
            <Dialog.Panel as={ModalComponent}>{children}</Dialog.Panel>
        </Dialog>
    );
}

export default Modal;
